import React, { useState, useEffect } from 'react';
import { XMarkIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

interface TourStep {
  id: string;
  title: string;
  content: string;
  target: string;
  position: 'top' | 'bottom' | 'left' | 'right';
}

interface PlatformTourProps {
  isActive: boolean;
  onComplete: () => void;
  onSkip: () => void;
}

const tourSteps: TourStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to Certs!',
    content: 'Welcome to Certs — your all-in-one platform for managing plumbing, heating, and gas work. Let\'s take a quick tour to show you how to stay organized, compliant, and ahead of schedule.',
    target: '.main-content',
    position: 'bottom'
  },
  {
    id: 'sidebar',
    title: 'Navigation Sidebar',
    content: 'This is your command center. Use the sidebar to switch between your main tools — from scheduling jobs and managing clients to handling invoices, certificates, and reports. Think of it as your digital van dashboard — everything you need is one click away.',
    target: '.sidebar',
    position: 'right'
  },
  {
    id: 'kpi-cards',
    title: 'Key Performance Indicators',
    content: 'Your KPI cards give you a live snapshot of how your business is performing today. Quickly see revenue, active jobs, unpaid invoices, and team activity — so you can spot wins and issues at a glance.',
    target: '.kpi-cards-section',
    position: 'bottom'
  },
  {
    id: 'financial-chart',
    title: 'Financial Overview',
    content: 'Here\'s where your business health comes to life. This chart tracks your income and expenses over time, helping you understand cash flow, seasonal trends, and profitability — all without a spreadsheet.',
    target: '.financial-chart-section',
    position: 'bottom'
  },
  {
    id: 'service-requests',
    title: 'Service Requests',
    content: 'When new customers reach out, their requests appear here. You can review details, prioritize urgent work, and assign the right engineer — all from this panel. No more missed calls or messy WhatsApp threads.',
    target: '.service-requests',
    position: 'top'
  },
  {
    id: 'engineer-spotlight',
    title: 'Top Performing Engineer',
    content: 'Meet your MVP of the week! This section highlights your top-performing engineer based on customer satisfaction, completed jobs, and response times — great for motivation and recognition.',
    target: '.engineer-spotlight',
    position: 'left'
  },
  {
    id: 'job-assignment',
    title: 'Smart Job Assignment',
    content: 'Let Certs handle the heavy lifting. Our smart assignment tool recommends the best engineer for each job based on distance, skill set, and availability — saving you time and fuel.',
    target: '.assignment-table',
    position: 'top'
  },
  {
    id: 'compliance',
    title: 'Compliance Tracking',
    content: 'Never miss a renewal again. Certs automatically tracks all your certificates, reminders, and safety documents — keeping your business compliant and audit-ready with zero stress.',
    target: '.compliance-card',
    position: 'left'
  },
  {
    id: 'help-widget',
    title: 'Need Help?',
    content: 'Need a hand? Tap the Help Widget anytime (bottom-right). You\'ll find guides, tutorials, and quick access to live support — we\'re here to keep your business running smoothly.',
    target: '.help-widget',
    position: 'top'
  }
];

export default function PlatformTour({ isActive, onComplete, onSkip }: PlatformTourProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 100, left: 100 });
  
  // Debug logging
  console.log('PlatformTour render:', { isActive, currentStep, tourStepsLength: tourSteps.length, tooltipPosition });

  useEffect(() => {
    if (isActive && currentStep < tourSteps.length) {
      const step = tourSteps[currentStep];
      
      // Wait a bit for the DOM to be ready
      const timer = setTimeout(() => {
        const targetElement = document.querySelector(step.target);
        
        if (targetElement) {
          // Remove previous highlights
          document.querySelectorAll('.tour-highlight').forEach(el => {
            el.classList.remove('tour-highlight');
          });
          
          const rect = targetElement.getBoundingClientRect();
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
          
          let top = 0;
          let left = 0;
          
          switch (step.position) {
            case 'top':
              top = Math.max(50, rect.top + scrollTop - 20);
              left = Math.max(50, Math.min(window.innerWidth - 350, rect.left + scrollLeft + rect.width / 2));
              break;
            case 'bottom':
              top = Math.max(50, rect.bottom + scrollTop + 20);
              left = Math.max(50, Math.min(window.innerWidth - 350, rect.left + scrollLeft + rect.width / 2));
              break;
            case 'left':
              top = Math.max(50, rect.top + scrollTop + rect.height / 2);
              left = Math.max(50, rect.left + scrollLeft - 320);
              break;
            case 'right':
              top = Math.max(50, rect.top + scrollTop + rect.height / 2);
              left = Math.min(window.innerWidth - 350, rect.right + scrollLeft + 20);
              break;
          }
          
          console.log('Setting tooltip position:', { top, left, rect, step: step.position });
          setTooltipPosition({ top, left });
          
          // Highlight the target element
          targetElement.classList.add('tour-highlight');
          
          // Add special class for sidebar to prevent white background
          if (step.target === '.sidebar') {
            targetElement.classList.add('sidebar');
          }
          
          // Scroll to element if needed
          targetElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center',
            inline: 'center'
          });
        } else {
          console.warn(`Tour target not found: ${step.target}`);
          console.log('Available elements:', document.querySelectorAll('*[class*="kpi"], *[class*="financial"], *[class*="service"], *[class*="engineer"], *[class*="assignment"], *[class*="compliance"]'));
          // If target not found, position tooltip in center of screen
          const centerTop = window.innerHeight / 2;
          const centerLeft = window.innerWidth / 2;
          console.log('Centering tooltip:', { centerTop, centerLeft });
          setTooltipPosition({ 
            top: centerTop, 
            left: centerLeft 
          });
        }
      }, 100);
      
      return () => {
        clearTimeout(timer);
      };
    }
    
    return () => {
      // Remove highlights from all elements
      document.querySelectorAll('.tour-highlight').forEach(el => {
        el.classList.remove('tour-highlight');
      });
    };
  }, [isActive, currentStep]);

  const handleNext = () => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      // Clean up highlights before completing
      document.querySelectorAll('.tour-highlight').forEach(el => {
        el.classList.remove('tour-highlight');
      });
      onComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = () => {
    // Clean up highlights before closing
    document.querySelectorAll('.tour-highlight').forEach(el => {
      el.classList.remove('tour-highlight');
    });
    onSkip();
  };

  if (!isActive || currentStep >= tourSteps.length) {
    console.log('Tour not rendering:', { isActive, currentStep, tourStepsLength: tourSteps.length });
    return null;
  }
  
  console.log('Tour IS rendering:', { isActive, currentStep, tooltipPosition });

  const step = tourSteps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === tourSteps.length - 1;

  return (
    <>
      {/* Overlay */}
      <div className="tour-overlay" />
      
      {/* Tooltip */}
      <div 
        style={{
          position: 'fixed',
          top: '200px',
          left: '200px',
          zIndex: 10000,
          background: 'white',
          border: '2px solid red',
          borderRadius: '12px',
          padding: '20px',
          maxWidth: '320px',
          boxShadow: '0 12px 32px rgba(0, 0, 0, 0.25)',
        }}
      >
        <div className="tour-content">
          <div className="tour-header">
            <h3 className="tour-title">{step.title}</h3>
            <button onClick={handleSkip} className="tour-close">
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
          
          <p className="tour-description">{step.content}</p>
          
          <div className="tour-footer">
            <div className="tour-progress">
              <span className="step-counter">
                {currentStep + 1} of {tourSteps.length}
              </span>
              <div className="progress-bar">
                <div 
                  className="progress-fill"
                  style={{ width: `${((currentStep + 1) / tourSteps.length) * 100}%` }}
                />
              </div>
            </div>
            
            <div className="tour-actions">
              <button 
                onClick={handleSkip}
                className="tour-btn secondary"
              >
                Skip Tour
              </button>
              
              {!isFirstStep && (
                <button 
                  onClick={handlePrevious}
                  className="tour-btn secondary"
                >
                  <ChevronLeftIcon className="w-4 h-4" />
                  Previous
                </button>
              )}
              
              <button 
                onClick={handleNext}
                className="tour-btn primary"
              >
                {isLastStep ? 'Finish' : 'Next'}
                {!isLastStep && <ChevronRightIcon className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
        
        {/* Arrow */}
        <div className={`tour-arrow arrow-${step.position}`} />
      </div>
    </>
  );
}
